/* ==========================================================================
   PIELLE 2.0 · assets/pielle-ux.js
   Convierte los bloques existentes en componentes con estado, SIN tocar copy:
   las tarjetas que ya están en el HTML se MUEVEN dentro de un tab / stepper /
   timeline / slider. No se crea, edita ni borra un solo texto.
   Además: resolución real de imágenes y reveal al scroll.
   El estado compacto del header vive en pielle-orbit.js (un solo dueño).
   Vanilla, sin dependencias. Respeta prefers-reduced-motion.
   ========================================================================== */
(function () {
  'use strict';
  var RM = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var pad = function (n) { return (n < 10 ? '0' : '') + n; };

  function mk(tag, cls, html) {
    var e = document.createElement(tag);
    if (cls) e.className = cls;
    if (html != null) e.innerHTML = html;
    return e;
  }
  function pick(card, sels) {
    for (var i = 0; i < sels.length; i++) {
      var n = card.querySelector(sels[i]);
      if (n && (n.textContent || '').trim()) return n;
    }
    return null;
  }

  /* =========================================================
     TABS · STEPPER · TIMELINE  (un motor, tres presentaciones)
     ========================================================= */
  function buildPanel(host, cfg) {
    if (!host || host.dataset.plxReady) return;
    var cards = Array.prototype.filter.call(host.children, function (n) { return n.nodeType === 1; });
    if (cards.length < 2) return;
    host.dataset.plxReady = '1';
    host.classList.add('plx-host');

    var uid = 'plx' + Math.random().toString(36).slice(2, 8);
    var dark = !!host.closest('.mechanism, .urgency, .cta');
    var box = mk('div', 'plx plx--' + cfg.mode + (dark ? ' plx--dark' : ''));
    var rail = mk('div', 'plx__rail');
    rail.setAttribute('role', 'tablist');
    if (cfg.mode !== 'tabs') rail.setAttribute('aria-orientation', 'horizontal');
    var stage = mk('div', 'plx__stage');
    var meta = mk('div', 'plx__meta');
    var count = mk('span', 'plx__count');
    var bar = mk('div', 'plx__bar', '<i></i>');
    meta.appendChild(count); meta.appendChild(bar);

    var tabs = [], panels = [];

    cards.forEach(function (card, i) {
      var labelNode = pick(card, cfg.label);
      var text = labelNode ? labelNode.textContent.trim() : String(i + 1);
      if (labelNode) labelNode.classList.add('plx-moved');

      var b = mk('button', 'plx__tab');
      b.type = 'button';
      b.id = uid + '-t' + i;
      b.setAttribute('role', 'tab');
      b.setAttribute('aria-controls', uid + '-p' + i);
      b.appendChild(mk('span', 'plx__tab-n', pad(i + 1)));
      b.appendChild(mk('span', 'plx__tab-l', '')).textContent = text;
      if (cfg.mode === 'timeline' || cfg.mode === 'stepper') b.appendChild(mk('span', 'plx__dot'));

      var p = mk('div', 'plx__panel');
      p.id = uid + '-p' + i;
      p.setAttribute('role', 'tabpanel');
      p.setAttribute('aria-labelledby', b.id);
      p.appendChild(card);

      rail.appendChild(b); stage.appendChild(p);
      tabs.push(b); panels.push(p);
      b.addEventListener('click', function () { go(i, true); });
      b.addEventListener('keydown', function (ev) {
        var d = ev.key === 'ArrowRight' ? 1 : ev.key === 'ArrowLeft' ? -1 : 0;
        if (!d) return;
        ev.preventDefault();
        go((i + d + tabs.length) % tabs.length, true, true);
      });
    });

    box.appendChild(rail); box.appendChild(stage);
    if (cfg.meta !== false) box.appendChild(meta);
    host.appendChild(box);

    var cur = -1;
    function go(i, user, focus) {
      if (i === cur) return;
      cur = i;
      tabs.forEach(function (t, k) {
        var on = k === i;
        t.classList.toggle('is-active', on);
        t.setAttribute('aria-selected', on ? 'true' : 'false');
        t.tabIndex = on ? 0 : -1;
      });
      panels.forEach(function (p, k) {
        var on = k === i;
        p.classList.toggle('is-active', on);
        p.setAttribute('aria-hidden', on ? 'false' : 'true');
      });
      count.innerHTML = '<b>' + pad(i + 1) + '</b><i>/</i>' + pad(tabs.length);
      bar.style.setProperty('--p', ((i + 1) / tabs.length * 100) + '%');
      if (focus) tabs[i].focus();
      if (user && tabs[i].scrollIntoView) {
        try { tabs[i].scrollIntoView({ block: 'nearest', inline: 'center', behavior: RM ? 'auto' : 'smooth' }); } catch (e) {}
      }
      sharpenSoon(box);
    }
    go(0);

    var x0 = null, y0 = null;
    stage.addEventListener('touchstart', function (e) { x0 = e.touches[0].clientX; y0 = e.touches[0].clientY; }, { passive: true });
    stage.addEventListener('touchend', function (e) {
      if (x0 == null) return;
      var dx = e.changedTouches[0].clientX - x0, dy = e.changedTouches[0].clientY - y0;
      if (Math.abs(dx) > 55 && Math.abs(dx) > Math.abs(dy) * 1.6) go(Math.min(tabs.length - 1, Math.max(0, cur + (dx < 0 ? 1 : -1))), true);
      x0 = null;
    }, { passive: true });
  }

  /* =========================================================
     SLIDER · arrastre real, flechas, contador y progreso

     10/09/2026: el control ahora se oculta solo cuando la pista no tiene
     nada que desplazar. Antes se pintaba igual y quedaba una flecha muerta
     — le pasaba a #ph .proofWomen, que el bloque declara como grid de una
     sola columna: las tarjetas se apilan y scrollWidth == clientWidth.
     La decisión se toma en paint(), que corre al cargar, al scrollear, al
     redimensionar y cuando terminan de cargar fuentes e imágenes; así que
     si la pista empieza a desbordar más tarde, el control reaparece.
     ========================================================= */
  function buildSlider(track, cfg) {
    if (!track || track.dataset.plxReady) return;
    var items = Array.prototype.filter.call(track.children, function (n) { return n.nodeType === 1; });
    if (items.length < 2) return;
    track.dataset.plxReady = '1';
    track.classList.add('plx-track');
    // control manual: si el bloque traía una marquesina CSS, se apaga. No puede
    // girar solo y ofrecer flechas al mismo tiempo.
    track.style.animation = 'none';

    var wrap = track.parentNode;
    wrap.classList.add('plx-slider');
    var ctr = mk('div', 'plx-ctrl');
    var prev = mk('button', 'plx-arrow plx-arrow--prev', '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M15 5l-7 7 7 7"/></svg>');
    var next = mk('button', 'plx-arrow plx-arrow--next', '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 5l7 7-7 7"/></svg>');
    prev.type = next.type = 'button';
    prev.setAttribute('aria-label', 'Anterior'); next.setAttribute('aria-label', 'Siguiente');
    var bar = mk('div', 'plx-bar', '<i></i>');
    var cnt = mk('span', 'plx-count');
    ctr.appendChild(prev); ctr.appendChild(bar); ctr.appendChild(cnt); ctr.appendChild(next);
    wrap.appendChild(ctr);

    function step() {
      var a = items[0].getBoundingClientRect();
      var b = items[1] ? items[1].getBoundingClientRect() : a;
      return Math.max(1, (b.left - a.left) || a.width);
    }
    function paint() {
      var max = track.scrollWidth - track.clientWidth;
      /* nada que deslizar → sin control. No se muestran flechas muertas. */
      var idle = max <= 4;
      ctr.style.display = idle ? 'none' : '';
      wrap.classList.toggle('plx-slider--idle', idle);
      if (idle) return;
      var p = max > 0 ? track.scrollLeft / max : 0;
      var i = Math.round(track.scrollLeft / step());
      var per = Math.max(1, Math.round(track.clientWidth / step()));
      var total = Math.max(1, items.length - per + 1);
      cnt.innerHTML = '<b>' + pad(Math.min(total, i + 1)) + '</b><i>/</i>' + pad(total);
      bar.style.setProperty('--p', (p * 100).toFixed(1) + '%');
      prev.disabled = track.scrollLeft < 4;
      next.disabled = track.scrollLeft > max - 4;
    }
    function slide(d) {
      var x = track.scrollLeft + d * step() * (cfg && cfg.page ? Math.max(1, Math.floor(track.clientWidth / step())) : 1);
      try { track.scrollTo({ left: x, behavior: RM ? 'auto' : 'smooth' }); } catch (e) { track.scrollLeft = x; }
    }
    prev.addEventListener('click', function () { slide(-1); });
    next.addEventListener('click', function () { slide(1); });
    var tm = null;
    track.addEventListener('scroll', function () { if (tm) return; tm = setTimeout(function () { tm = null; paint(); sharpenSoon(track); }, 70); }, { passive: true });

    var down = false, sx = 0, sl = 0, moved = 0;
    track.addEventListener('pointerdown', function (e) {
      if (e.pointerType === 'touch') return;
      down = true; moved = 0; sx = e.clientX; sl = track.scrollLeft;
      track.classList.add('is-grabbing');
    });
    window.addEventListener('pointermove', function (e) {
      if (!down) return;
      var dx = e.clientX - sx; moved = Math.abs(dx);
      if (moved > 3) { e.preventDefault(); track.scrollLeft = sl - dx; }
    }, { passive: false });
    window.addEventListener('pointerup', function () {
      if (!down) return;
      down = false; track.classList.remove('is-grabbing');
      if (moved > 6) {
        var s = step();
        var x = Math.round(track.scrollLeft / s) * s;
        try { track.scrollTo({ left: x, behavior: RM ? 'auto' : 'smooth' }); } catch (e) { track.scrollLeft = x; }
      }
    });
    track.addEventListener('click', function (e) { if (moved > 6) { e.preventDefault(); e.stopPropagation(); } }, true);
    track.addEventListener('dragstart', function (e) { if (down) e.preventDefault(); });

    paint();
    setTimeout(paint, 700); setTimeout(paint, 1800);
    window.addEventListener('resize', paint, { passive: true });
    window.addEventListener('load', paint);
    if (window.ResizeObserver) new ResizeObserver(paint).observe(track);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(paint);
  }

  /* =========================================================
     Mapa de bloques → componente
     .pp4__companions, .roadmap-wrap y #ph .timelineCards siguen listados pero
     hoy los desactiva pielle-orbit.js marcando data-plx-ready antes de este
     init. Un solo dueño por nodo.

     #ph .proofWomen salió de SLIDERS: el bloque lo declara como grid de una
     sola columna, o sea tres tarjetas apiladas. Nunca hubo nada que deslizar.
     Apiladas se leen las tres de una, que es como está diseñado el bloque.
     ========================================================= */
  var PANELS = [
    { s: '.pp4__companions', mode: 'tabs',     label: ['h4', 'h3', 'b'] },
    { s: '.pm4__grid',       mode: 'stepper',  label: ['b', 'h3'] },
    { s: '.roadmap-wrap',    mode: 'timeline', label: ['.timeline-time', 'em'] },
    { s: '#ph .sceneGrid',      mode: 'stepper',  label: ['b', 'h3'] },
    { s: '#ph .mechVisualGrid', mode: 'stepper',  label: ['b', 'h3'] },
    { s: '#ph .timelineCards',  mode: 'timeline', label: ['em', 'h3'] }
  ];
  var SHOWCASE = ['.pledad__g', '#ph .audVisualGrid'];
  var SLIDERS = [
    { s: '.rv4__track' }, { s: '.pielle-bundles__reviews-track' },
    { s: '.pielle-cierre-gal__track', page: true }, { s: '.pielle-wa__track', page: true }
  ];

  /* =========================================================
     RESOLUCIÓN REAL DE IMÁGENES
     El origen del pixelado no era object-fit: era el atributo `sizes`. Los
     bloques declaran anchos del layout viejo (356 px, 200 px, 92 px) y el
     navegador elige del srcset el candidato chico aunque el marco mida 520.
     Acá se corrige `sizes` con el ancho real medido; si ni el candidato más
     grande alcanza, se le pide al CDN el ancho exacto. El CDN nunca amplía
     por encima del original: esto no inventa calidad, deja de tirarla.
     ========================================================= */
  var SHARP = '#ph img,.plx__panel img,.plx-show img,.pledad img,.pm4 img,.pp4 img,' +
              '.pr4 img,.rv4 img,.plorb img,.timeline-image,.pielle-cierre-gal__item img,' +
              '.cmp img,.pu4 img,.pielle-bundles__review-card img';
  function sharpen(root) {
    var dpr = Math.min(window.devicePixelRatio || 1, 2);
    (root || document).querySelectorAll(SHARP).forEach(function (img) {
      try {
        var box = img.getBoundingClientRect().width ||
                  (img.parentElement && img.parentElement.getBoundingClientRect().width) || 0;
        if (box < 24) return;
        var want = Math.min(1600, Math.ceil(box * dpr / 100) * 100);
        if (img.dataset.plSharp && parseInt(img.dataset.plSharp, 10) >= want) return;
        var base = img.getAttribute('src') || '';
        if (base.indexOf('cdn.shopify.com') < 0) return;
        var ss = img.getAttribute('srcset') || '';
        var ws = (ss.match(/width=(\d+)/g) || []).map(function (w) { return parseInt(w.slice(6), 10); });
        var max = ws.length ? Math.max.apply(null, ws) : 0;
        img.dataset.plSharp = String(want);
        if (max >= want) { img.setAttribute('sizes', Math.round(box) + 'px'); return; }
        if (!/[?&]width=\d+/.test(base)) return;
        img.removeAttribute('srcset');
        img.removeAttribute('sizes');
        img.src = base.replace(/([?&]width=)\d+/, '$1' + want);
      } catch (e) {}
    });
  }
  var shTimer;
  function sharpenSoon(root) {
    clearTimeout(shTimer);
    shTimer = setTimeout(function () { sharpen(root); }, 140);
  }

  /* =========================================================
     REVEAL AL SCROLL
     Discreto, sólo para lo que todavía no se ve, y con red de seguridad: si
     el observer no existe no se aplica nada, y si algo falla queda visible.
     ========================================================= */
  var RV = '#ph > section > .w > *,.pr4__layout,.cmp-card,.plorb,.roadmap-wrap > *,' +
           '.pielle-extra__faq,.pledad__g,.rv4__head,.pu4,.pc4,.faq-final .pielle-faq-titulo';
  function reveal(root) {
    if (RM || !('IntersectionObserver' in window)) return;
    var els = Array.prototype.slice.call((root || document).querySelectorAll(RV))
      .filter(function (e) {
        if (e.dataset.plRv) return false;
        var r = e.getBoundingClientRect();
        return r.top > window.innerHeight * 0.92 && r.height > 24;
      });
    if (!els.length) return;
    var io = new IntersectionObserver(function (ents) {
      ents.forEach(function (en) {
        if (!en.isIntersecting) return;
        var el = en.target;
        var i = el.parentElement ? Array.prototype.indexOf.call(el.parentElement.children, el) : 0;
        el.style.setProperty('--d', Math.min(i, 4) * 70 + 'ms');
        el.classList.add('is-in');
        io.unobserve(el);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.04 });
    els.forEach(function (e) { e.dataset.plRv = '1'; e.classList.add('pl-rv'); io.observe(e); });
    setTimeout(function () { els.forEach(function (e) { e.classList.add('is-in'); }); }, 6000);
  }


  /* =========================================================
     DURACIÓN DEL PACK EN EL PICKER  (10/09/2026)

     El caption del picker es un solo texto —"Total $72.990 · 60 ml ·
     10 semanas"— y el tramo de la duración no tiene nodo propio, así que
     NO hay forma de resaltarlo desde CSS. Acá se envuelve únicamente ese
     tramo en un <span> con peso 700 y color Plum.

     EL COPY NO SE TOCA: el texto que se muestra queda idéntico carácter por
     carácter. Lo único que cambia es el peso y el color de ese tramo.

     Sólo actúa si lo que va después del último "·" contiene la palabra
     semana/semanas. Si Facu cambia ese texto por otra cosa, no marca nada
     en vez de marcar lo que no corresponde.

     El theme reescribe estos captions cuando cambia la variante, así que un
     MutationObserver lo vuelve a aplicar. Se desconecta mientras escribe.
     ========================================================= */
  var DUR_RE = /semanas?/i;
  var MIDOT = '·';

  function markDuration(cap) {
    if (!cap || cap.querySelector('.pl-dur')) return;
    var nodes = cap.childNodes, last = null;
    for (var i = nodes.length - 1; i >= 0; i--) {
      if (nodes[i].nodeType === 3 && nodes[i].nodeValue.trim()) { last = nodes[i]; break; }
    }
    if (!last) return;
    var t = last.nodeValue;
    var k = t.lastIndexOf(MIDOT);
    if (k < 0) return;
    var tail = t.slice(k + 1);
    if (!DUR_RE.test(tail)) return;
    var lead = (tail.match(/^\s*/) || [''])[0];
    var s = document.createElement('span');
    s.className = 'pl-dur';
    s.style.fontWeight = '700';
    s.style.color = '#271A1E';
    s.textContent = tail.slice(lead.length);
    last.nodeValue = t.slice(0, k + 1) + lead;
    if (last.nextSibling) last.parentNode.insertBefore(s, last.nextSibling);
    else last.parentNode.appendChild(s);
  }

  var durObs = null, durBusy = false;
  function durations() {
    if (durBusy) return;
    var caps = document.querySelectorAll('.quantity-break__caption');
    if (!caps.length) return;
    durBusy = true;
    try {
      if (durObs) durObs.disconnect();
      Array.prototype.forEach.call(caps, function (c) { try { markDuration(c); } catch (e) {} });
      if (window.MutationObserver) {
        if (!durObs) durObs = new MutationObserver(function () { setTimeout(durations, 0); });
        document.querySelectorAll('quantity-breaks,.quantity-breaks-container').forEach(function (h) {
          durObs.observe(h, { childList: true, characterData: true, subtree: true });
        });
      }
    } catch (e) {} finally { durBusy = false; }
  }

  function init(root) {
    root = root || document;
    PANELS.forEach(function (c) {
      root.querySelectorAll(c.s).forEach(function (h) { try { buildPanel(h, c); } catch (e) {} });
    });
    SLIDERS.forEach(function (c) {
      root.querySelectorAll(c.s).forEach(function (t) { try { buildSlider(t, c); } catch (e) {} });
    });
    SHOWCASE.forEach(function (sel) {
      root.querySelectorAll(sel).forEach(function (g) {
        if (g.dataset.plxShow) return; g.dataset.plxShow = '1';
        g.classList.add('plx-show');
      });
    });
    root.querySelectorAll('.pielle-extra__faq, .pielle-bundles__faq, #ph .faq').forEach(function (g) {
      if (g.dataset.plxAcc) return; g.dataset.plxAcc = '1';
      g.addEventListener('click', function (e) {
        var sum = e.target.closest('summary'); if (!sum) return;
        var d = sum.parentNode;
        if (!d.open) g.querySelectorAll('details[open]').forEach(function (o) { if (o !== d) o.open = false; });
      });
    });
    sharpen(root);
    root.querySelectorAll(SHARP).forEach(function (img) {
      if (img.complete || img.dataset.plHook) return;
      img.dataset.plHook = '1';
      img.addEventListener('load', function () { sharpenSoon(); }, { once: true });
    });
    try { durations(); } catch (e) {}
    try { reveal(root); } catch (e) {}
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { init(); });
  else init();
  window.addEventListener('load', function () { sharpen(); try { durations(); } catch (e) {} try { reveal(); } catch (e) {} });
  window.addEventListener('resize', function () { sharpenSoon(); });
  window.addEventListener('scroll', function () { sharpenSoon(); }, { passive: true });
  setTimeout(function () { sharpen(); }, 1200);
  setTimeout(function () { sharpen(); }, 3000);
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', function (e) { init(e.target); });
  }
  window.PielleUX = { init: init, panel: buildPanel, slider: buildSlider, sharpen: sharpen, reveal: reveal, durations: durations };
})();